import React, { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import Select from 'react-select';
import { MessageType, OptionType } from 'models/BaseModel';
import { Code } from 'constants/codes';
import { Utility } from 'helpers/UtilityHelpers';
import { API } from 'helpers/ApiHelpers';
import { Company } from 'models/CompanyModel';
import { Location } from 'models/LocationModel';
import DOMPurify from "dompurify";
import { Doctor } from 'models/DoctorModel';
import { ExamTemplate } from 'models/ExamTemplateModel';
import e from 'express';
import { Files } from 'helpers/PrintImports';



interface ValueFieldProps {
  title?: string;
  value?: string | number | null;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  titleBold?: boolean;
  valueBold?: boolean;
}

const ValueField = ({
  title,
  value,
  size,
  className = '',
  titleBold = true,
  valueBold = false,
}: ValueFieldProps) => {

  ////console.log("ValueField Value:",value);

  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
      <div className={`${className ? className : 'fvalue'}`}>
        {title ? <span className={`title ${titleBold ? 'bold':''}`}>{title}</span> : null}
        {value ? <span className={`value ${valueBold ? 'bold':''}`}>{value}</span> : null}
      </div>
    </div>
  );
};

interface DisplayFieldProps {
  title?: string;
  value?: string | number | null;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  titleBold?: boolean;
  valueBold?: boolean;
  borderTop?: boolean;
  borderBottom?: boolean;
}

const DisplayField = ({
  title,
  value,
  size,
  className = '',
  titleBold = false,
  valueBold = false,
  borderTop = false,
  borderBottom = false,
}: DisplayFieldProps) => (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
    <div className={`${className ? className : 'fdisplay'} ${borderTop ? 'btop':''} ${borderBottom ? 'bbottom':''}`}>
      {title ? <span className={`title ${titleBold ? 'bold':''}`}>{title}</span> : null}
      {value ? <span className={`value ${valueBold ? 'bold':''}`}>{value}</span> : null}
    </div>
  </div>
);

interface TotalFieldProps {
  title?: string;
  value?: number | null;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  titleBold?: boolean;
  valueBold?: boolean;
  borderTop?: boolean;
  borderBottom?: boolean;
}

const TotalField = ({
  title,
  value,
  size,
  className = '',
  titleBold = true,
  valueBold = true,
  borderTop = false,
  borderBottom = false,
}: TotalFieldProps) => (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
    <div className={`${className ? className : 'ftotal'} ${borderTop ? 'btop':''} ${borderBottom ? 'bbottom':''}`}>
      {title ? <span className={`title ${titleBold ? 'bold':''}`}>{title}</span> : null}
      {<span className={`value ${valueBold ? 'bold':''}`}>{value ? value.toFixed(2) : "0.00"}</span>}
    </div>
  </div>
);

interface DoctorSignatureProps {
  doctorId?: string | number;
}

const DoctorSignature : React.FC<DoctorSignatureProps> = ({ doctorId }) => {
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);
  const [signUrl, setSignUrl] = useState<string | null >(null);
  const [imagePath, setImagePath] = useState<string>('');


  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!doctorId) return; 

      // if("18,22,23,24,26,28,29,36,44,46,47,48,53,54,56,59".includes(doctorId.toString())){
      //    setImagePath(`/assets/images/sign/sign${doctorId}.png`);
      // }

      // //console.log("Doctor ID for signature:", doctorId,imagePath);

      try {
        const doctor = await API.Data.GetDoctor(doctorId);
        setDoctorData(doctor);
       
        const fileList = doctor?.file_list;
        if (fileList && fileList.length > 0) {
          const sign = await Utility.Image.Blob("signature", fileList);
          setSignUrl(sign);
        }

      } catch (error) {
        console.error("Error fetching doctor data:", error);
      }
    };

    fetchCompanyData();
  }, [doctorId]);

  return (
    <div className='doctor-sign'>
      <div className="sign">
        {/* {imagePath && <img src={imagePath} alt="Doctor Sign" />} */}
        {signUrl ? <img src={signUrl} alt="Doctor Sign" /> : null}
      </div>
      <div className="name">{`${doctorData?.doctor_first_name} ${doctorData?.doctor_middle_name} ${doctorData?.doctor_last_name}`}</div>
      <div className="license">{doctorData?.doctor_license_number ? `License: #${doctorData?.doctor_license_number}` : null}</div>
    </div>
  );
};

interface PrintedOnFieldProps {
  fixed?: boolean
}

const PrintedOnField = ({
  fixed = true
}: PrintedOnFieldProps) => {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  //const formattedDate = new Date().toLocaleString("en-US", options);

  return (
    <div className={`poweredby ${fixed ? 'fixed' : ''}`}>
        <span>Powered by:</span> 
        <img src="/assets/images/basic/next-vizon.png" alt="NextVizon Optometry Software" /> 
        <span>Optometry Software</span>
    </div>
    // <div className="print-date" data-printedon={`${formattedDate}`} >
    //   {`Printed on: ${formattedDate}`}
    // </div>
  );
};


interface TextFieldProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  size?: [number, number, number, string?] | null | undefined;
  width?: number;
  require?: boolean;
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  placeholder?: string;
  defaultValue?: string;
  alignRight?: boolean;
  maxLength?: number;
  minLength?: number;
}

const TextField = ({
  title,
  id,
  value,
  onChange,
  onFocus,
  size,
  width = 60,
  require = false,
  className = '',
  disabled = false,
  horizontal = false,
  placeholder='',
  defaultValue='',
  alignRight = false,
  maxLength,
  minLength,
}: TextFieldProps) => {
  const iconClass = Utility.Get.Icon(id);
  const controlWidth = width;
  const titleWidth = 100 - width;
  
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={horizontal ? 'htext' : 'ftext'}>
        {title && (
          <label 
            htmlFor={id} 
            className={horizontal ? `flcol form-col${titleWidth}` : ''}>
              {title} {require && "*"}
          </label>
        )} 
        <input
          className={
            `${horizontal ? `flcol form-col${controlWidth}` : ''} ${alignRight ? 'tright' : ''} ${require ? 'req' : ''} ${className} ${iconClass}`
          }
          id={id}
          type='text'
          value={value ?? ''}
          onChange={onChange}
          {...(onFocus ? { onFocus } : {})}
          {...(defaultValue ? { 'data-default': defaultValue } : {})}
          title={value?.toString() ?? title ?? ''}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          minLength={minLength}
          autoComplete="off"
        />
      </div>
    </div>
  );
};

type CheckFieldProps = {
  title?: string;
  id?: string;
  value?: string | number | null;
  size?: [number, number, number, string?] | null | undefined;
  width?: number;
  info?: string;
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  isCross?: boolean;
  isWarning?: boolean;
  isLight?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const CheckField: React.FC<CheckFieldProps> = ({ 
  title,
  id,
  value,
  size,
  width = 10,
  info = '',
  className = '',
  disabled = false,
  horizontal = false,
  isCross = false,
  isWarning = false,
  isLight = false,
  onChange,
}) => {
  const controlWidth = width;
  const titleWidth = 100 - width;
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"} ${className}`}>
      <div className={horizontal ? 'hcheck' : 'fcheck'}>
        {title && (
          <label 
            htmlFor={id} 
            className={horizontal ? `flcol form-col${titleWidth}` : ''}>
              {title}
          </label>
        )} 
        <input
          className={`form-select ${isCross ? 'cross' : ''} ${isWarning ? 'warning' : ''} ${isLight ? 'light' : ''}`}
          id={id}
          type="checkbox"
          checked={value === '1'}
          onChange={onChange}
          disabled={disabled}
          title={info}
        />
        
      </div>
    </div>
  );
};

type CheckSingleProps = {
  title?: string;
  id?: string;
  size?: [number, number, number, string?] | null | undefined;
  width?: number;
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  alignRight?: boolean;
  isChecked?: boolean; // now optional
  setIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
};

const CheckSingle: React.FC<CheckSingleProps> = ({ 
  title,
  id,
  size,
  width = 10,
  className = '',
  disabled = false,
  horizontal = false,
  alignRight = false,
  isChecked = false,
  setIsChecked,
}) => {
  const controlWidth = width;
  const titleWidth = 100 - width;
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={`${horizontal ? 'hcheck' : 'fcheck'} ${alignRight ? 'tright' : 'tleft'}`}>
        <input
          className={
            `select ${horizontal ? `form-col${controlWidth}` : ''} ${alignRight ? 'fright' : 'fleft'} ${className}`
          }
          id={id}
          type="checkbox"
          checked={isChecked ?? false}
          onChange={(e) => setIsChecked(e.target.checked)}
          disabled={disabled}
        />
        {title && (
          <label 
            htmlFor={id} 
            className={`${horizontal ? ` form-col${titleWidth}` : ''} ${alignRight ? 'frcol' : 'flcol'}`}>
              {title}
          </label>
        )} 
      </div>
    </div>
  );
};

interface SuggestionFieldProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: [number, number, number, string?] | null | undefined;
  width?: number;
  require?: boolean;
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  placeholder?: string;
  dataList?: string;
  defaultValue2?: string;
}

const SuggestionField = ({
  title,
  id,
  value,
  onChange,
  size,
  width = 60,
  require = false,
  className = '',
  disabled = false,
  horizontal = false,
  placeholder = '',
  dataList = '',
  defaultValue2 = '',
}: SuggestionFieldProps) => {
  const iconClass = ''; // Replace with: Utility.Get.Icon(id);
  const controlWidth = width;
  const titleWidth = 100 - width;

  const [inputValue, setInputValue] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const suggestionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Sync external value to local state
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (
      highlightIndex >= 0 &&
      suggestionRefs.current[highlightIndex]
    ) {
      suggestionRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth"
      });
    }
  }, [highlightIndex]);

  const allSuggestions = dataList
    .split(';')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    if (input.classList.contains('slist')) {
      //console.log('get focused');
    }

    if (dataList) {
      const filtered = allSuggestions.filter((item) =>
        item.toLowerCase().includes(inputValue.toString().toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);

    // Trigger parent onChange
    if (onChange && id) {
      const inputElement = document.createElement('input');
      inputElement.id = id;
      inputElement.name = id;
      inputElement.value = suggestion;

      const syntheticEvent = {
        target: inputElement,
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
  };
  
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={`${horizontal ? 'htext' : 'ftext'} ${className}`}>
        {title && (
          <label 
            htmlFor={id} 
            className={horizontal ? `flcol form-col${titleWidth}` : ''}>
              {title} {require && "*"}
          </label>
        )} 
        <input
          className={
            `${dataList ? 'slist' : ''} ${horizontal ? `flcol form-col${controlWidth}` : ''} ${require ? 'req' : ''} ${iconClass}`
          }
          id={id}
          type='text'
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            
            // Filter suggestions based on input
            if (dataList) {
              const filtered = allSuggestions.filter((item) =>
                item.toLowerCase().includes(val.toLowerCase())
              );
              setSuggestions(filtered);
              setShowSuggestions(filtered.length > 0);
              setHighlightIndex(-1);
            }

            onChange?.(e);
          }}
          onKeyDown={(e) => {
            if (!showSuggestions) return;

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlightIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
              if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
                e.preventDefault();
                handleSuggestionClick(suggestions[highlightIndex]);
              }
            } else if (e.key === 'Escape') {
              setShowSuggestions(false);
              setHighlightIndex(-1);
            }
          }}
          title={title || inputValue.toString() }
          {...(disabled ? { disabled: true } : {})}
          {...(placeholder ? { 'placeholder': placeholder } : {})}
          {...(dataList ? { 'data-list': dataList } : {})}
          onFocus={handleFocus}
          onBlur={() => {
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightIndex(-1);
          }}
          {...(defaultValue2 ? { 'data-default': defaultValue2 } : {})}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul>
            {suggestions.map((suggestion, idx) => (
              <li
                key={idx}
                ref={(el) => (suggestionRefs.current[idx] = el)}
                onMouseDown={() => handleSuggestionClick(suggestion)}
                className={`suggestion-item ${idx === highlightIndex ? 'highlighted' : ''}`}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface SuggestionAreaProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  size?: [number, number, number, string?] | null | undefined;
  width?: number;
  require?: boolean;
  className?: string;
  disabled?: boolean;
  horizontal?: boolean;
  placeholder?: string;
  dataList?: string;
  defaultValue?: string;
}

const SuggestionArea = ({
  title,
  id,
  value,
  onChange,
  size,
  width = 60,
  require = false,
  className = '',
  disabled = false,
  horizontal = false,
  placeholder = '',
  dataList = '',
  defaultValue = '',
}: SuggestionAreaProps) => {
  const iconClass = ''; // Replace with: Utility.Get.Icon(id);
  const controlWidth = width;
  const titleWidth = 100 - width;

  const [inputValue, setInputValue] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const suggestionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      if(inputValue ==''){
        textareaRef.current.style.height = '33px';
      }
      else
      {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }
  }, [inputValue]);

  // Sync external value to local state
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (
      highlightIndex >= 0 &&
      suggestionRefs.current[highlightIndex]
    ) {
      suggestionRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth"
      });
    }
  }, [highlightIndex]);

  const allSuggestions = dataList
    .split(';')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.currentTarget;
    

    if (input.classList.contains('slist')) {
      //console.log('get focused');
    }

    if (dataList) {
      const filtered = allSuggestions.filter((item) =>
        item.toLowerCase().includes(inputValue.toString().toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    }

    if (textareaRef.current) {
      if(inputValue ==''){
        textareaRef.current.style.height = '33px';
      }
      else
      {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }

  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);

    // Trigger parent onChange
    if (onChange && id) {
      const inputElement = document.createElement('input');
      inputElement.id = id;
      inputElement.name = id;
      inputElement.value = suggestion;

      const syntheticEvent = {
        target: inputElement,
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
  };
  
  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={`${horizontal ? 'harea' : 'farea'} ${className}`}> 
        {title && (
          <label 
            htmlFor={id} 
            className={horizontal ? `flcol form-col${titleWidth}` : ''}>
              {title} {require && "*"}
          </label>
        )} 
        <textarea
          className={
            `ofyn ${dataList ? 'slist' : ''} ${horizontal ? `flcol form-col${controlWidth}` : ''} ${require ? 'req' : ''} ${iconClass}`
          }
          ref={textareaRef}
          id={id}
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            
            // Filter suggestions based on input
            if (dataList) {
              const filtered = allSuggestions.filter((item) =>
                item.toLowerCase().includes(val.toLowerCase())
              );
              setSuggestions(filtered);
              setShowSuggestions(filtered.length > 0);
              setHighlightIndex(-1);
            }

            onChange?.(e);
          }}
          onKeyDown={(e) => {
            if (!showSuggestions) return;

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlightIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
              if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
                e.preventDefault();
                handleSuggestionClick(suggestions[highlightIndex]);
              }
            } else if (e.key === 'Escape') {
              setShowSuggestions(false);
              setHighlightIndex(-1);
            }
          }}
          title={title || inputValue.toString() }
          {...(disabled ? { disabled: true } : {})}
          {...(placeholder ? { 'placeholder': placeholder } : {})}
          {...(dataList ? { 'data-list': dataList } : {})}
          onFocus={handleFocus}
          onBlur={() => {
            setSuggestions([]);
            setShowSuggestions(false);
            setHighlightIndex(-1);
          }}
          {...(defaultValue ? { 'data-default': defaultValue } : {})}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul>
            {suggestions.map((suggestion, idx) => (
              <li
                key={idx}
                ref={(el) => (suggestionRefs.current[idx] = el)}
                onMouseDown={() => handleSuggestionClick(suggestion)}
                className={`suggestion-item ${idx === highlightIndex ? 'highlighted' : ''}`}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface ColorFieldProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  require?: boolean;
  className?: string;
  disabled?: boolean;
}

const ColorField = ({
  title,
  id,
  value,
  onChange,
  size,
  require = false,
  className = '',
  disabled = false,
}: ColorFieldProps) => {
  const iconClass = Utility.Get.Icon(title);
  return (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={`ftext`}>
        {title ? <label htmlFor={id}>{title} {require && "*"}</label> : null } 
        <input
          className={`${require ? 'req':''} ${className} ${iconClass}`}
          id={id}
          type='color'
          value={value ?? ''}
          onChange={onChange}
          title={title}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

interface AmountFieldProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  require?: boolean;
  className?: string;
  disabled?: boolean;
  align?: typeof Code.Align[keyof typeof Code.Align];
}

const AmountField = ({
  title,
  id,
  value,
  onChange,
  size,
  require,
  className = '',
  disabled = false,
  align = Code.Align.LEFT,
}: AmountFieldProps) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
    <div className={`ftext`}>
      {title ? <label htmlFor={id}>{title} {require && "*"}</label> : null } 
      <input
        className={`icon-space icon-amount ${alignClass} ${require && "req"} ${className} `}
        id={id}
        type='number'
        value={Number(value ?? 0) || 0}
        onChange={onChange}
        title={title}
        disabled={disabled}
      />
    </div>
  </div>
  );
};

interface NumberFieldProps {
  title?: string;
  id?: string;
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  require?: boolean;
  className?: string;
  disabled?: boolean;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  align?: typeof Code.Align[keyof typeof Code.Align];
}

const NumberField = ({
  title,
  id,
  value,
  onChange,
  size,
  require,
  className = '',
  disabled = false,
  minValue = 0,
  maxValue,
  defaultValue = 0,
  align = Code.Align.LEFT,
}: NumberFieldProps) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
    <div className={`ftext`}>
      {title ? <label htmlFor={id} className={`${alignClass}`}>{title} {require && "*"}</label> : null } 
      <input
        className={`${alignClass} ${require && "req"} ${className} `}
        id={id}
        type='number'
        value={value ?? defaultValue}
        onChange={onChange}
        min={minValue}
        {...(maxValue !== undefined ? { max: maxValue } : {})}
        title={title}
        disabled={disabled}
      />
    </div>
  </div>
  );
};

interface DateFieldProps {
  title?: string;
  id: string;
  value?: Date | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  width?: number;
  require?: boolean;
  currentDate?: boolean;
  className?: string;
  horizontal?: boolean;
  disabled?: boolean;
  selected?: string | null;
}

const DateField = ({
  title,
  id,
  value,
  onChange,
  size,
  width = 60,
  require,
  currentDate = false,
  className = '',
  horizontal = false,
  disabled = false,
  selected = ''
}: DateFieldProps) => {
  let formattedDate: string = '';

  if (value) {
    const dateString = value.toString();
    if (dateString.includes("GMT")) {
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        formattedDate = parsedDate.toISOString().split('T')[0];
      }
    }
    else if (dateString.includes("T")) {
      formattedDate = dateString.split('T')[0];
    } 
    else {
      formattedDate = dateString;
    }
    if (formattedDate === "1900-01-01") {
      formattedDate = "";
    }
  } else if (selected) {
    formattedDate = selected;
  } else if (currentDate) {
    formattedDate =  Utility.Date.getLocalDate().toISOString().split('T')[0];
    //formattedDate =  new Date().toISOString().split('T')[0];
  } 

  const controlWidth = width;
  const titleWidth = 100 - width;

  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
      <div className={horizontal ? 'htext' : 'ftext'}>
        {title && (
          <label 
            htmlFor={id} 
            className={horizontal ? `flcol form-col${titleWidth}` : ''}>
            {title} {require && "*"}
          </label>
        )}
        <input
          className={
            `${horizontal ? `flcol form-col${controlWidth}` : ''} ${require ? 'req' : ''} ${className}`
          }
          id={id}
          type="date"
          value={formattedDate} 
          onChange={onChange}
          title={formattedDate ?? title}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

interface TimeFieldProps {
  title: string;
  id: string;
  value: string | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  require?: boolean;
  currentTime?: boolean;
  className?: string;
}

const TimeField = ({
  title,
  id,
  value,
  onChange,
  size,
  require,
  currentTime = false,
  className = '',
}: TimeFieldProps) => {

  let newTime = value;

  if(newTime){
    const date = new Date(`1900-01-01T${newTime}`);
    newTime = Utility.Format.Date(date,'HH:mm');//.replaceAll("12:00","")
  }
  else{
    if(currentTime) newTime = Utility.Format.Date(new Date(),'HH:mm');
  }


  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
      <div className="ftext">
        <label htmlFor={id}>{title} {require && "*"}</label>
        <input
          className={`${require && "req"} ${className}`}
          id={id}
          type="time"
          value={newTime} 
          onChange={onChange}
          title={title}
        />
      </div>
    </div>
  );
};


interface TextAreaProps {
  title?: string;
  id: string;
  value?: string | null;
  height?: number;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  width?: number;
  require?: boolean;
  titleClass?: string;
  controlClass?: string;
  horizontal?: boolean;
  defaultValue?: string;
}

const TextArea = ({
  title = '',
  id,
  value,
  height = 80,
  onChange,
  size,
  width = 60,
  require,
  titleClass ='',
  controlClass ='',
  horizontal = false,
  defaultValue = ''
}: TextAreaProps) =>  { 
  
  const controlWidth = width;
  const titleWidth = 100 - width;

  return (
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
      <div className={horizontal ? 'harea' : 'farea'}>
        {title &&  (
          <label 
            className={
              `${horizontal ? `flcol form-col${titleWidth}` : ''}  ${titleClass}`
            }
            htmlFor={id}>
              {title} {require && "*"}
          </label> 
        )} 
        <textarea
          className={
            `${horizontal ? `flcol form-col${controlWidth}` : ''} ${require && "req"} ${controlClass}`
          }
          id={id}
          value={value ?? ''}
          onChange={onChange}
          autoComplete="off"
          title={title}
          {...(defaultValue ? { 'data-default': defaultValue } : {})}
          style={{ minHeight: `${height}px` }}
        />
      </div>
    </div>
  );
};

interface PasswordFieldProps {
  title: string;
  id: string;
  value?: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  className?: string;
  keyId: string;
}

const PasswordField = ({
  title,
  id,
  value,
  onChange,
  size,
  className = '',
  keyId,
}: PasswordFieldProps) => (
  <div className={`${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"}`}>
    <div className='ftext'>
      <label htmlFor={id}>{title}</label>
      <input
        className={`${keyId === Code.Key.NEW ? "req" : ""} ${className}`}
        id={id}
        type='text'
        value={value ?? ''}
        onChange={onChange}
        title={title}
      />
    </div>
  </div>
);

interface SelectFieldProps {
  id: string;
  value?: string | number | null;
  options: { value: string | number; label: string }[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  size?: [number,number,number,string?] | null | undefined;
  width?: number;
  title?: string;
  message?: string;
  require?: boolean;
  className?: string;
  horizontal?: boolean;
  disabled?: boolean;
  selected?: number | string | null;
}

const SelectField = ({
  id,
  value,
  options,
  onChange,
  size,
  width = 60,
  title,
  message,
  require,
  className = '',
  horizontal = false,
  disabled = false,
  selected = 0,
}: SelectFieldProps) => { 
  const controlWidth = width;
  const titleWidth = 100 - width;
  
  return(
    <div className={`${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={horizontal ? 'hoption' : 'foption'}>
        <label 
          htmlFor={id}
          className={horizontal ? `flcol form-col${titleWidth}` : ''}>
            {title} {require && "*"}
        </label>
        <select 
          className={
            `${horizontal ? `flcol form-col${controlWidth}` : ''} ${require ? 'req' : ''} ${className}`
          }
          id={id} 
          value={value && value !== '' ? value : selected ?? 0}
          onChange={onChange} 
          title={message ? message : title}
          disabled={disabled}
          autoComplete="off"
          >
          {options.map((item) => (
            <option 
              key={item.value} 
              value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

interface FilterSelectProps {
  id: string;
  options: OptionType[];
  value?: number | string | null;
  defaultValue?: number | null;
  setFormData?: React.Dispatch<React.SetStateAction<any>>; // Using 'any' for flexibility
  size?: [number,number,number,string?] | null | undefined;
  title?: string;
  message?: string;
  require?: boolean;
  className?: string,
  setSelectedId?: React.Dispatch<React.SetStateAction<any>>;
  setSelectedName?: React.Dispatch<React.SetStateAction<any>>;
}

const FilterSelect: React.FC<FilterSelectProps> = ({ 
  id, 
  options, 
  value, 
  defaultValue, 
  setFormData, 
  size, 
  title, 
  message, 
  require, 
  className,
  setSelectedId,
  setSelectedName,
}) => {
  const handleChange = (selected: OptionType | null) => {
    const selectedValue = selected ? selected.value : null;
    const selectedLabel = selected ? selected.label : null;

    if(setFormData){
      setFormData((prev: any) => ({
        ...prev,
        [id]: selectedValue, 
      }));
    }
    if (setSelectedId) {
      setSelectedId(selectedValue);
    }
    if (setSelectedName) {
      setSelectedName(selectedLabel);
    }
  };

  //console.log("Selected value:)", value);


  return (
    <div className={`fl ${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`}>
      <div className={`foption ${require && 'req'} ${className ? className : ''}`} title={message ? message : title}>
        {title ? <label htmlFor={id}>{title} {require && "*"}</label> : null }
        <Select
          options={options}
          onChange={handleChange}
          value={
            options?.find(option => option.value == value) || 
            options?.find(option => option.value == defaultValue) || 
            options?.[0] ||
            null
          }
          isSearchable={true}
          menuPlacement="auto"
          styles={Code.Style.FormOption}
        />
      </div>
     </div>
  );
};

interface MultiSelectProps {
  title: string;
  id: string;
  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  options: OptionType[];
  size?: [number,number,number,string?] | null | undefined;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ 
  title, 
  id, 
  selectedItems, 
  setSelectedItems, 
  options: AllItemList,
  size, 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  let selectedItemLabels = selectedItems
  .map(itemValue => AllItemList.find(item => item.value.toString() === itemValue)?.label)
  .join(", ");

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setSelectedItems((prevSelected) =>
      checked ? [...prevSelected, value] : prevSelected.filter(v => v !== value)
    );
  };

  const toggleDropdown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`droplist ${size ? Utility.Convert.formColumnStyle(size) : "form-col100"}`} ref={dropdownRef}>
      {title ? <title>{title}</title> : null }
      <button id={`${id.toLowerCase()}_list`} className="toggle" type="button" onClick={toggleDropdown}>
       {selectedItemLabels || "Select"}
      </button>

      {isDropdownOpen && (
        <div className="list">
          {AllItemList.map((item) => (
            <div key={item.value}>
              <label>
                <input
                  type="checkbox"
                  value={item.value}
                  checked={selectedItems.includes(item.value.toString())}
                  onChange={handleCheckboxChange}
                />
                {item.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface FieldHeaderProps {
  id: string;
  title: string;
  size?: [number,number,number,string?] | null | undefined;
  require?: boolean;
  className?: string;
  children: React.ReactNode;
}

const FieldHeader: React.FC<FieldHeaderProps> = ({ 
  id, 
  title, 
  size, 
  require = false, 
  className='', 
  children 
}) => {
  return (
    <div className={`foption  ${size ? Utility.Convert.formColumnStyle(size) : "fl form-col100"} ${require ? 'req':''} ${className ? className:''}`} title={title}>
      <label htmlFor={id}>{title} {require && "*"}</label>
      {children}
    </div>
  );
};

interface FormTitleProps {
  size?: [number,number,number,string?] | null | undefined;
  title?: string;
  align?: typeof Code.Align[keyof typeof Code.Align];
  space?: boolean;
  className?: string;
}

const FormTitle: React.FC<FormTitleProps> = ({ 
  size, 
  title, 
  space = false, 
  align = Code.Align.LEFT,
  className= '',
}) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`common-label ${space ? 'left-label':''} ${className} ${alignClass} ${size ? Utility.Convert.formColumnStyle(size) : "fl col100"}`}>
      {title? title : "\u00A0"}
    </div>
  );
};

interface FormContainerTitleProps {
  size?: [number,number,number,string?] | null | undefined;
  title?: string;
  align?: typeof Code.Align[keyof typeof Code.Align];
  space?: boolean;
  className?: string;
}

const ContainerTitle: React.FC<FormContainerTitleProps> = ({ 
  size, 
  title, 
  space = false, 
  align = Code.Align.LEFT, 
  className='',
}) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`container-title ${space ? 'left-label':''}  ${alignClass} ${className} ${size ? Utility.Convert.formColumnStyle(size) : "fl col100"}`}>
      {title? title.toUpperCase() : "\u00A0"}
    </div>
  );
};

interface FormContainerHeaderProps {
  size?: [number,number,number,string?] | null | undefined;
  title?: string;
  align?: typeof Code.Align[keyof typeof Code.Align];
  space?: boolean;
  className?: string;
}

const ContainerHeader: React.FC<FormContainerHeaderProps> = ({ 
  size, 
  title, 
  space = false, 
  align = Code.Align.LEFT, 
  className='',
}) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`container-header ${space ? 'left-label':''}  ${alignClass} ${className} ${size ? Utility.Convert.formColumnStyle(size) : "fl col100"}`}>
      <span>{title? title.toUpperCase() : "\u00A0"}</span>
    </div>
  );
};

interface FormContainerHeaderActionsProps {
  size?: [number,number,number,string?] | null | undefined;
  title?: string;
  align?: typeof Code.Align[keyof typeof Code.Align];
  space?: boolean;
  className?: string;
  groupId: number;
  fieldList?: string[];
  setExamData?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  isExpend?: boolean;
  setIsExpend?: React.Dispatch<React.SetStateAction<boolean>>;
  dateField?: string;
  setMessage: Dispatch<SetStateAction<MessageType>>
}

const ContainerHeaderActions: React.FC<FormContainerHeaderActionsProps> = ({ 
  size, 
  title, 
  space = false, 
  align = Code.Align.LEFT, 
  className='',
  groupId = 0,
  fieldList = [],
  setExamData,
  isExpend = true,
  setIsExpend = null,
  dateField = '',
  setMessage,
}) => {
  const alignClass = Utility.Get.alignClass(align);
  const [examTemplateData, setExamTemplateData] = useState<ExamTemplate[]>([]);
  const destinationGroupId = groupId.toString();

  const handleExpend = () => {
    if(setIsExpend){
      if(isExpend) setIsExpend(false); else setIsExpend(true);
    }
  };

  // Handle copy field values to clipboard
  const handleCopy = () => {
    if (!setExamData) return;
    let copyData = `groupid::${groupId};;`;
    fieldList.forEach((field, index) => {
      if (index >  0) {
        const inputElement = document.getElementById(field) as HTMLInputElement | null;
        if (inputElement?.value) {
          copyData += `${field}::${inputElement.value};;`;
        }
      }
    });
    navigator.clipboard.writeText(btoa(copyData));
    //navigator.clipboard.writeText(copyData);
  };

  const handlePaste = async () => {
    let data = await navigator.clipboard.readText();
    if (!data) return;
    if (!setExamData) return;
    
    if (!/^[A-Za-z0-9+/=]+$/.test(data)) {
      //console.log("Clipboard data is not valid Base64.");
      return;
    }

    data = atob(data);

   
    if(!data.includes("::") || !data.includes(";;")) return;

    let entries = data.split(";;").filter(Boolean); 

    
    if (entries.length === 0) return;

    const [sourceGroupId, sourceGroupValue] = entries[0].split("::");

    console.clear();

    //console.log("1--------------------->", sourceGroupValue, destinationGroupId);

    if(sourceGroupValue !== destinationGroupId){
      const sourceFields = Utility.Get.GroupMapFields(Number(sourceGroupValue))
      const destinationFields = Utility.Get.GroupMapFields(Number(destinationGroupId))

      data = replaceField(data, sourceFields.ODSphere, destinationFields.ODSphere);
      data = replaceField(data, sourceFields.OSSphere, destinationFields.OSSphere);
      data = replaceField(data, sourceFields.ODCylinder, destinationFields.ODCylinder);
      data = replaceField(data, sourceFields.OSCylinder, destinationFields.OSCylinder);
      data = replaceField(data, sourceFields.ODAxis, destinationFields.ODAxis);
      data = replaceField(data, sourceFields.OSAxis, destinationFields.OSAxis);
      data = replaceField(data, sourceFields.ODAdd, destinationFields.ODAdd);
      data = replaceField(data, sourceFields.OSAdd, destinationFields.OSAdd);
      data = replaceField(data, sourceFields.ODPd, destinationFields.ODPd);
      data = replaceField(data, sourceFields.OSPd, destinationFields.OSPd);
      data = replaceField(data, sourceFields.ODAcuity, destinationFields.ODAcuity);
      data = replaceField(data, sourceFields.OSAcuity, destinationFields.OSAcuity);
      data = replaceField(data, sourceFields.OUAcuity, destinationFields.OUAcuity);

      data = replaceField(data, sourceFields.ODPrism, destinationFields.ODPrism);
      data = replaceField(data, sourceFields.ODOrient, destinationFields.ODOrient);
      data = replaceField(data, sourceFields.OSPrism, destinationFields.OSPrism);
      data = replaceField(data, sourceFields.OSOrient, destinationFields.OSOrient);
      data = replaceField(data, sourceFields.ODPrism2, destinationFields.ODPrism2);
      data = replaceField(data, sourceFields.ODOrient2, destinationFields.ODOrient2);
      data = replaceField(data, sourceFields.OSPrism2, destinationFields.OSPrism2);
      data = replaceField(data, sourceFields.OSOrient2, destinationFields.OSOrient2);

      data = replaceField(data, sourceFields.Lens, destinationFields.Lens);
      data = replaceField(data, sourceFields.MatteTinted, destinationFields.MatteTinted);
      data = replaceField(data, sourceFields.Method, destinationFields.Method);
      data = replaceField(data, sourceFields.Notes, destinationFields.Notes);
      data = replaceField(data, sourceFields.RxDate, destinationFields.RxDate);
      data = replaceField(data, sourceFields.RxExpiry, destinationFields.RxExpiry);

      data = replaceField(data, sourceFields.DxOD, destinationFields.DxOD);
      data = replaceField(data, sourceFields.DxOS, destinationFields.DxOS);
      data = replaceField(data, sourceFields.DxOU, destinationFields.DxOU);
      data = replaceField(data, sourceFields.NxOD, destinationFields.NxOD);
      data = replaceField(data, sourceFields.NxOS, destinationFields.NxOS);
      data = replaceField(data, sourceFields.NxOU, destinationFields.NxOU);
      data = replaceField(data, sourceFields.IxOD, destinationFields.IxOD);
      data = replaceField(data, sourceFields.IxOS, destinationFields.IxOS);
      data = replaceField(data, sourceFields.IxOU, destinationFields.IxOU);

      data = replaceField(data, sourceFields.ODSupplier, destinationFields.ODSupplier);
      data = replaceField(data, sourceFields.OSSupplier, destinationFields.OSSupplier);
      data = replaceField(data, sourceFields.ODType, destinationFields.ODType);
      data = replaceField(data, sourceFields.OSType, destinationFields.OSType);
      data = replaceField(data, sourceFields.ODName, destinationFields.ODName);
      data = replaceField(data, sourceFields.OSName, destinationFields.OSName);
      data = replaceField(data, sourceFields.ODColor, destinationFields.ODColor);
      data = replaceField(data, sourceFields.OSColor, destinationFields.OSColor);
      data = replaceField(data, sourceFields.ODPack, destinationFields.ODPack);
      data = replaceField(data, sourceFields.OSPack, destinationFields.OSPack);
      data = replaceField(data, sourceFields.ODBc, destinationFields.ODBc);
      data = replaceField(data, sourceFields.OSBc, destinationFields.OSBc);
      data = replaceField(data, sourceFields.ODDia, destinationFields.ODDia);
      data = replaceField(data, sourceFields.OSDia, destinationFields.OSDia);
      data = replaceField(data, sourceFields.Status, destinationFields.Status);
      data = replaceField(data, sourceFields.ExpiryDate, destinationFields.ExpiryDate);
      data = replaceField(data, sourceFields.FittingNote, destinationFields.FittingNote);
      data = replaceField(data, sourceFields.Solution, destinationFields.Solution);

      //console.log("2--------------------->", sourceFields, destinationFields);

      entries = data.split(";;").filter(Boolean); 

      if (entries.length === 0) return;
    }

    let isUpdated = false;
    
    setExamData(prev => {
      const updated = { ...prev };

      entries.forEach(entry => {
        const [key, value] = entry.split("::");
        if (!key || value == null) return; 

        fieldList.forEach((field, index) => {
          if(field == key)
          {
            if (updated[key] === '' || updated[key] === ' ' || updated[key] === undefined || updated[key] === null) 
            {
              updated[key] = value;
              isUpdated = true;
            }
          }
        });

        if(isUpdated && dateField){
            const userName = Utility.Storage.UserName();
            updated[dateField] = userName + ' ' + Utility.Format.Date(new Date(), Code.Format.DateTime.Full);
        }
      
      });

      return updated;
    });
    
    

  };
  
  function replaceField(
    data: string, 
    sourceValue: string | undefined, 
    destinationValue: string | undefined
  ): string {
    if (sourceValue && sourceValue !== '' && destinationValue && destinationValue !== '') {
      return data.replace(sourceValue, destinationValue);
    }
    return data;
  }

  const handlePasteOld = async () => {
    let data = await navigator.clipboard.readText();
    if (!data) return;
    if (!setExamData) return;
    
    if (!/^[A-Za-z0-9+/=]+$/.test(data)) {
      //console.log("Clipboard data is not valid Base64.");
      return;
    }

    data = atob(data);

    if(!data.includes("::") || !data.includes(";;")) return;

    const entries = data.split(";;").filter(Boolean); 
    if (entries.length === 0) return;
    
    setExamData(prev => {
      const updated = { ...prev };

      if(dateField) {
        const userName = Utility.Storage.UserName();
        updated[dateField] = userName + ' ' + Utility.Format.Date(new Date(), Code.Format.DateTime.Full);
      }

      entries.forEach(entry => {
        const [key, value] = entry.split("::");
        if (!key || value == null) return; 
        
        if (updated[key] === '' || updated[key] === undefined || updated[key] === null) 
        {
          updated[key] = value;
        }
      });
      return updated;
    });
  };

  //handle auto-fill field values
  const handleFill = () => {
    if (!setExamData) return;

    setExamData(prev => {
      const updated = { ...prev };

      if (dateField) {
          const userName = Utility.Storage.UserName();
          updated[dateField] = userName + ' ' + Utility.Format.Date(new Date(), Code.Format.DateTime.Full);
        }

      fieldList.forEach((field, index) => {
        if(index > 0) {
          const inputElement = document.getElementById(field) as HTMLInputElement | null;
          if (inputElement) {
            const defaultValue = inputElement.getAttribute("data-default") || "";
            if(defaultValue)
            {
              if (updated[field] === '' || updated[field] === undefined || updated[field] === null) {
                updated[field] = defaultValue;
              }
            }
          }
        }
      });
      return updated;
    });
  };


  //handle clear field values
  const handleClear = () => {
    if (!setExamData) return;

    setExamData(prev => {
      const updated = { ...prev };
      if (dateField) {
        const userName = Utility.Storage.UserName();
        updated[dateField] = userName + ' ' + Utility.Format.Date(new Date(), Code.Format.DateTime.Full);
      }

      fieldList.forEach((field, index) => {
          if(index > 0) {
            updated[field] = " ";
          }
      });
      return updated;
    });
  };

  const handleImportExamTemplates = () => {
    if(!examTemplateData || examTemplateData.length === 0){
      const examValues = Utility.Storage.Get(Code.LocalKey.examValues);
      if(!examValues) { 
        Utility.Message.Warning([`Exam template is not available`], setMessage); 
        return; 
      }
      const parsedExamValues = JSON.parse(atob(examValues)) as Record<string, ExamTemplate>;
      setExamTemplateData(Object.values(parsedExamValues));
    }
    else{
      setExamTemplateData([]);
    }
  };

  
  const fillExamTemplateDataIntoFields = (examTemplateData: string | undefined) => {
    if(examTemplateData){
      if (!setExamData) return;
      let parsedExamValues = JSON.parse(examTemplateData) as ExamTemplate;
      if(!parsedExamValues || Object.keys(parsedExamValues).length === 0) return;

      let isUpdated = false;

      setExamData(prev => {
        const updated = { ...prev };

        Object.entries(parsedExamValues).forEach(([key, value]) => {
          if(fieldList.includes(key) && !updated[key]){
            updated[key] = value;
            isUpdated = true;
          }
        });

        if(isUpdated && dateField){
          const userName = Utility.Storage.UserName();
          updated[dateField] = userName + ' ' + Utility.Format.Date(new Date(), Code.Format.DateTime.Full);
        }

        return updated;
      });

      

      setTimeout(() => {
        setExamTemplateData([]);
      }, 100);
    }
  };

  return (
    <div className={`container-header ${space ? 'left-label':''}  ${alignClass} ${className} ${size ? Utility.Convert.formColumnStyle(size) : "fl col100"}`}>
      <span>{title? title.toUpperCase() : "\u00A0"}</span>
      <div className="fright">
        {isExpend && (
          <>
            <span className="icon-btn copybtn" onClick={handleCopy} title="Copy"></span>
            <span className="icon-btn pastebtn" onClick={handlePaste} title="Paste"></span>
            <span className="icon-btn fillbtn" onClick={handleImportExamTemplates}  title="Show Exam Templates"></span>
            <span className="icon-btn deletebtn" onClick={handleClear} title="Clear Values"></span>
            <div className='exam-template-list'>
              {examTemplateData.map((item) => (
                <a key={item.examtemplate_id} onClick={() => fillExamTemplateDataIntoFields(item.examtemplate_data)}>
                  {item.examtemplate_name}
                </a>
              ))}
            </div>
          </>
        )}
        {setIsExpend != null && <span className="expendbtn" onClick={handleExpend} title="Expend"></span>}
      </div>
    </div>
  );
};

interface FormSubHeadProps {
  title?: string;
  align?: typeof Code.Align[keyof typeof Code.Align];
}

const FormSubHead : React.FC<FormSubHeadProps> = ({ title, align = Code.Align.LEFT }) => {
  const alignClass = Utility.Get.alignClass(align);
  return (
    <div className={`print-subhead ${alignClass}`}>
      {title?.toUpperCase()}
    </div>
  );
};

interface CompanyHeadProps {
  companyId?: number | undefined;
  locationId?: number | undefined;
}

const FormCompanyHead : React.FC<CompanyHeadProps> = ({ companyId, locationId }) => {
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [locationData, setLocationData] = useState<Location | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null >(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId || !locationId) return; 
      try {
        const company = await API.Data.GetCompany(companyId);
        setCompanyData(company);
        const location = await API.Data.GetLocation(locationId);
        setLocationData(location);

        //Load user's profile photo
        const fileList = location?.file_list;
        if (fileList && fileList.length > 0) {
          const logo = await Utility.Image.Blob("logo", fileList);
          setLogoUrl(logo);
        }

      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    fetchCompanyData();
  }, [companyId, locationId]);

  return (
    <div className='print-company'>
      {logoUrl ? <div className="logo"><img src={logoUrl} alt="Company Logo" /></div> : <div className="name">{companyData?.company_legal_name}</div>}
      <div className="desc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(locationData?.location_print || "") }} />
    </div>
  );
};

interface DocumentFieldProps {
  fileUrl?: string | null;
}

const DocumentField: React.FC<DocumentFieldProps> = ({ fileUrl }) => {
  return fileUrl ? (
    <iframe 
      className="preview-file" 
      src={fileUrl} 
      onContextMenu={(e) => e.preventDefault()}
      title="Document Preview" 
      onLoad={(e) => {
        const iframe = e.currentTarget;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;

        if (!doc) return;

        const img = doc.querySelector("img");
        if (img) {
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.display = "block";
        }

        doc.body.style.margin = "0";
      }}
    />
  ) : (
    <p className="preview-unavailable">No file available for preview.</p>
  );
};

export const Field = {
  Title: FormTitle,
  ContainerTitle: ContainerTitle,
  ContainerHeader: ContainerHeader,
  ContainerHeaderActions: ContainerHeaderActions,
  Header: FieldHeader,
  CompanyHead: FormCompanyHead,
  DoctorSignature: DoctorSignature,
  SubHead: FormSubHead,
  Value: ValueField,
  Display: DisplayField,
  Total: TotalField,
  Text: TextField,
  Check: CheckField,
  CheckSingle: CheckSingle,
  Suggestion: SuggestionField,
  SuggestionArea: SuggestionArea,
  Color: ColorField,
  Number: NumberField,
  Amount: AmountField,
  Date: DateField,
  Time: TimeField,
  Area: TextArea,
  Document: DocumentField,
  Password: PasswordField,
  Select: SelectField,
  FilterSelect: FilterSelect,
  MultiSelect: MultiSelect,
  PrintedOn: PrintedOnField,
}